import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// 未保存の変更があるまま離れようとしたときに、確認を出すための仕組み。
// このアプリは BrowserRouter を使うため、useBlocker は使えない。次の3つを自前で捕まえる。
//   1. タブを閉じる・再読み込み（beforeunload。ブラウザの標準の確認が出る）
//   2. アプリ内のリンクのクリック（捕捉して止め、確認ダイアログを出す）
//   3. ブラウザの「戻る」（履歴に1つ足して受け止め、確認ダイアログを出す）
// 画面内のボタン（「戻る」など）で離れるときは、requestLeave を通す。
export function useUnsavedChangesGuard(dirty: boolean) {
  const navigate = useNavigate();
  const [pending, setPending] = useState<(() => void) | null>(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  // 確認を経て離れるとき、同じ仕組みで二重に止めないための印
  const bypass = useRef(false);
  const guardEntry = useRef(false);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onClick = (event: MouseEvent) => {
      if (bypass.current || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.target === "_blank" || anchor.origin !== window.location.origin) return;
      if (anchor.pathname === window.location.pathname && anchor.search === window.location.search) return;
      event.preventDefault();
      event.stopPropagation();
      const to = `${anchor.pathname}${anchor.search}${anchor.hash}`;
      setPending(() => () => navigate(to));
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [dirty, navigate]);

  useEffect(() => {
    if (!dirty) return undefined;
    // 「戻る」を、同じ画面の履歴へ吸収するために、履歴を1つ足す
    window.history.pushState({ futureBoardGuard: true }, "", window.location.href);
    guardEntry.current = true;
    const onPopState = () => {
      if (bypass.current) return;
      window.history.pushState({ futureBoardGuard: true }, "", window.location.href);
      setPending(() => () => window.history.go(-2));
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      // 保存などで未保存でなくなったら、足した履歴を戻す（画面を離れるときは何もしない）
      if (guardEntry.current && !bypass.current && window.history.state?.futureBoardGuard) {
        guardEntry.current = false;
        window.history.go(-1);
      }
    };
  }, [dirty]);

  /** 画面内のボタンで離れるとき。未保存なら確認を出し、そうでなければすぐに実行する。 */
  const requestLeave = useCallback((action: () => void) => {
    if (!dirtyRef.current) action();
    else setPending(() => action);
  }, []);

  /** 「破棄して離れる」「保存して離れる」のあとに呼ぶ。止めていた移動を実行する。 */
  const proceed = useCallback(() => {
    const action = pending;
    setPending(null);
    if (!action) return;
    bypass.current = true;
    action();
    // 移動が済んだあとで、印を戻す
    window.setTimeout(() => { bypass.current = false; }, 300);
  }, [pending]);

  /** 「編集を続ける」 */
  const cancel = useCallback(() => setPending(null), []);

  return { isConfirming: pending !== null, requestLeave, proceed, cancel };
}
